"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTransition } from "react";

import {
  clearAllFilters,
  removeFilterValue,
  type FilterChip,
} from "~/lib/storefront/filter-params";
import { cn } from "~/lib/utils";
import { useTranslator } from "~/i18n/provider";

/**
 * P2-11 · Active-filter chips — one removable chip per encoded value plus
 * "Clear all", mirroring the URL one-to-one (P2-DB02 §03). Client-side so
 * removal is instant; each edit resets to page 1 via the param helpers.
 */
export function ActiveFilterChips({ chips }: { chips: FilterChip[] }) {
  const t = useTranslator();
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const search = params.toString();

  if (chips.length === 0) return null;

  const push = (next: string) =>
    startTransition(() =>
      router.push(next ? `${pathname}?${next}` : pathname, { scroll: false }),
    );

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2",
        isPending && "opacity-60",
      )}
    >
      {chips.map((chip) => {
        // P15-20 · A BOOLEAN attribute chip carries its value as a message key
        // ("Machine-washable" + `browse.facetYes`), so the visible label and the
        // "Remove filter …" accessible name are composed here, once.
        const label = chip.valueLabelKey
          ? `${chip.label}: ${t(chip.valueLabelKey)}`
          : chip.label;
        return (
        <button
          key={chip.id}
          type="button"
          aria-label={`Remove filter ${label}`}
          onClick={() => {
            push(removeFilterValue(search, chip.param, chip.value));
          }}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-2 px-3 py-1 text-sm text-fg hover:border-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          {label}
          <span aria-hidden="true" className="text-fg-muted">
            ×
          </span>
        </button>
        );
      })}
      <button
        type="button"
        onClick={() => {
          push(clearAllFilters(search));
        }}
        className="text-sm text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {t("browse.clearAll")}
      </button>
    </div>
  );
}
